import directus, { type DirectusErrorType, type RequestQuery } from '@/features/api/directus';
import {
  aggregate,
  createItem,
  deleteItem,
  deleteItems,
  readItem,
  readItems,
  updateItem,
  updateItems,
} from '@directus/sdk';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type Result<T> = QueryReturnValue<T, DirectusErrorType, {}>;

export type ReadOneQueryArg<T, IdType extends string | number> =
  | IdType
  | { id: IdType; q?: RequestQuery<T> };

export function buildReadManyFn<T>(collection: string) {
  return async (q?: RequestQuery<T>): Promise<Result<T[]>> => {
    try {
      const data = await directus.request<T[]>(readItems(collection as never, q as never));
      return { data };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildReadOneFn<T, IdType extends string | number>(collection: string) {
  return async (params: ReadOneQueryArg<T, IdType>): Promise<Result<T>> => {
    try {
      const id =
        typeof params === 'object' && params !== null && 'id' in params ? params.id : params;
      const q =
        typeof params === 'object' && params !== null && 'q' in params ? params.q : undefined;
      const data = await directus.request<T>(readItem(collection as never, id, q as never));
      return { data };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildCountFn<T>(collection: string) {
  return async (q?: RequestQuery<T>): Promise<Result<number>> => {
    try {
      const data = await directus.request(
        aggregate(collection as never, {
          aggregate: { count: '*' },
          query: q,
        }),
      );
      return { data: Number((data as Array<{ count?: unknown }>)[0]?.count ?? 0) };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildCreateFn<T>(collection: string) {
  return async (body: Partial<T>): Promise<Result<T>> => {
    try {
      const data = await directus.request<T>(createItem(collection as never, body as never));
      return { data };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildUpdateFn<T, IdType extends string | number>(collection: string) {
  return async ({ id, body }: { id: IdType; body: Partial<T> }): Promise<Result<T>> => {
    try {
      const data = await directus.request<T>(updateItem(collection as never, id, body as never));
      return { data };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildUpdateManyFn<T, IdType extends string | number>(collection: string) {
  return async ({ ids, body }: { ids: IdType[]; body: Partial<T> }): Promise<Result<T[]>> => {
    try {
      const data = await directus.request<T[]>(
        updateItems(collection as never, ids as never, body as never),
      );
      return { data };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildDeleteFn<IdType extends string | number>(collection: string) {
  return async (id: IdType): Promise<Result<void>> => {
    try {
      await directus.request(deleteItem(collection as never, id));
      return { data: undefined };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}

export function buildDeleteManyFn<IdType extends string | number>(collection: string) {
  return async (ids: IdType[]): Promise<Result<void>> => {
    try {
      await directus.request(deleteItems(collection as never, ids as never));
      return { data: undefined };
    } catch (error: unknown) {
      return { error: error as DirectusErrorType };
    }
  };
}
